import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {testIdProps} from '@github-ui/test-id-props'
import {TriangleDownIcon} from '@primer/octicons-react'
import {
  Button,
  SelectPanel,
  type SelectPanelItemInput as ItemInput,
  type SelectPanelItemProps as ItemProps,
  useFocusTrap,
} from '@primer/react'
import debounce, {type DebouncedFunc} from 'lodash-es/debounce'
import {type ComponentProps, useCallback, useEffect, useRef, useState} from 'react'

import {apiSearchRepositories} from '../../api/repository/api-search-repositories'
import type {SuggestedRepository} from '../../api/repository/contracts'
import {getInitialState} from '../../helpers/initial-state'
import {useRepositories} from '../../state-providers/repositories/use-repositories'
import {Resources} from '../../strings'
import {RepositoryIcon} from '../fields/repository/repository-icon'
import {buildPickerLabel} from './helpers/accessible-label'
import {SEARCH_DEBOUNCE_DELAY_MS} from './helpers/search-constants'

type RepositoryPickerProps = ComponentProps<'div'> & {
  onRepositorySelected: (repo: SuggestedRepository) => void
  onNoSuggestedRepositories?: () => void
  isEditing?: boolean
  targetRepositoryId?: number
  shouldReset?: boolean
  headerDescription?: string
}

const getRepoName = (repo: SuggestedRepository) => {
  const {projectOwner} = getInitialState()
  const repoOwner = repo.nameWithOwner.split('/')[0]
  return repoOwner?.toLowerCase() !== projectOwner?.login.toLowerCase() ? repo.nameWithOwner : repo.name
}

// TODO: Refactor this component and the repo-suggestions component to share the logic for searching and setting repo
export const RepositoryPicker: React.FC<RepositoryPickerProps> = ({
  targetRepositoryId,
  isEditing,
  onRepositorySelected,
  onNoSuggestedRepositories,
  shouldReset = true,
  headerDescription,
}) => {
  const searchRef = useRef<HTMLInputElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState<string>('')
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [selected, setSelected] = useState<ItemInput | undefined>(undefined)

  const [repositories, setRepositories] = useState<Array<SuggestedRepository>>([])
  const [suggestedRepositories, setSuggestedRepositories] = useState<Array<SuggestedRepository>>([])
  const debouncedSearch = useRef<DebouncedFunc<() => Promise<void>> | undefined>(undefined)
  const {suggestRepositories} = useRepositories()

  // non render states
  const suggestedRepositoriesRef = useRef(suggestedRepositories)
  const fetchCountRef = useRef(0)
  const loadingRef = useRef(false)

  useFocusTrap({restoreFocusOnCleanUp: true, initialFocusRef: searchRef, containerRef: overlayRef})

  const onClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const fetchSuggestedRepos = useCallback(async () => {
    if (suggestedRepositoriesRef.current.length) return
    if (loadingRef.current) return

    loadingRef.current = true
    setLoading(true)

    const suggestedRepos = await suggestRepositories({repositoryId: targetRepositoryId})
    fetchCountRef.current += 1
    if (suggestedRepos) suggestedRepositoriesRef.current = suggestedRepos
    if (suggestedRepos) setSuggestedRepositories(suggestedRepos)

    loadingRef.current = false
    setLoading(false)
  }, [suggestRepositories, targetRepositoryId])

  const getRepository = useCallback(
    (repoId: number) => {
      return (query !== '' ? repositories : suggestedRepositories).find(repo => repo.id === repoId)
    },
    [query, repositories, suggestedRepositories],
  )

  const resetRepositorySelection = useCallback(() => {
    const locatedRepository = suggestedRepositories.find(repo => repo.id === targetRepositoryId)
    const finalRepository = locatedRepository ? locatedRepository : suggestedRepositories[0]
    if (finalRepository) {
      // Set default selection to first item or target from props
      setSelected({
        id: finalRepository.id,
        text: getRepoName(finalRepository),
        leadingVisual: () => RepositoryIcon({repository: finalRepository}),
      })
      onRepositorySelected(finalRepository)
    }
  }, [suggestedRepositories, targetRepositoryId, onRepositorySelected])

  // Set initial selected repo after fetching suggested repos
  useEffect(() => {
    if (!selected && shouldReset) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      resetRepositorySelection()
    }
  }, [resetRepositorySelection, selected, shouldReset])

  // Reset if the targetRepositoryId updates (e.g. discard)
  useEffect(() => {
    if (selected?.id !== targetRepositoryId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      resetRepositorySelection()
    }
  }, [resetRepositorySelection, selected?.id, targetRepositoryId])

  // fetch suggested repos on render
  useEffect(() => {
    if (loadingRef.current) return

    if (fetchCountRef.current === 0) {
      fetchSuggestedRepos()
      // Re-fetch suggested repos if the targetRepositoryId is not in the list
    } else if (
      targetRepositoryId &&
      !suggestedRepositories.find(repo => repo.id === targetRepositoryId) &&
      fetchCountRef.current < 2
    ) {
      suggestedRepositoriesRef.current = []
      fetchSuggestedRepos()
    } else if (suggestedRepositoriesRef.current.length === 0 && onNoSuggestedRepositories) {
      onNoSuggestedRepositories()
    }
  }, [fetchSuggestedRepos, onNoSuggestedRepositories, suggestedRepositories, targetRepositoryId])

  const fetchRepos = useCallback(
    async (searchQuery: string) => {
      if (searchQuery !== '') {
        setLoading(true)
        const {repositories: searchedRepos} = await apiSearchRepositories({query: searchQuery})
        setRepositories(searchedRepos)
        setLoading(false)
      } else {
        setRepositories(suggestedRepositories ?? [])
      }
    },
    [suggestedRepositories],
  )

  const searchRepos = useCallback(
    (value: string) => {
      if (debouncedSearch.current) {
        debouncedSearch.current.cancel()
      }
      debouncedSearch.current = debounce(() => fetchRepos(value), SEARCH_DEBOUNCE_DELAY_MS)
      debouncedSearch.current()
    },
    [fetchRepos],
  )

  const onSearchChange = (value: string) => {
    setQuery(value)
    searchRepos(value)
  }

  const onSelectedChange = (selectedItem?: ItemInput) => {
    if (selectedItem?.id) {
      const repo = getRepository(selectedItem.id as number)
      if (repo) {
        setSelected({
          id: repo.id,
          text: getRepoName(repo),
          leadingVisual: () => RepositoryIcon({repository: repo}),
        })
        onRepositorySelected(repo)

        // TODO: postStats?
      }
    }
  }

  const displayedItems = (query !== '' ? repositories : suggestedRepositories).map<ItemProps>(repoItem => {
    const name = getRepoName(repoItem)

    return {
      ...repoItem,
      text: name,
      leadingVisual: () => RepositoryIcon({repository: repoItem}),
      selectionVariant: 'single',
      descriptionVariant: 'block',
      selected: undefined,
      onAction: (_, event) => {
        event.preventDefault()
        onSelectedChange(repoItem)
        onClose()
      },
    }
  })

  // Build the visible + accessible label:
  const label = buildPickerLabel(headerDescription, selected?.text)

  // Show loading skeleton while repos haven't been fetched yet or auto-selection is in progress
  const showLoadingSkeleton = suggestedRepositories.length === 0 || (shouldReset && !selected)

  return (
    <>
      {!showLoadingSkeleton ? (
        <SelectPanel
          placeholderText={Resources.repoPickerFilterPlaceholder}
          open={isOpen}
          title={Resources.repoListTitle}
          renderAnchor={({children, ...anchorProps}) => (
            <Button
              disabled={!isEditing}
              leadingVisual={selected?.leadingVisual}
              trailingVisual={TriangleDownIcon}
              {...anchorProps}
              {...testIdProps('repo-suggestions-button')}
              aria-label={label}
            >
              {/* eslint-disable-next-line i18n-text/no-en */}
              {children || 'Select repository'}
            </Button>
          )}
          onOpenChange={open => setIsOpen(open)}
          loading={loading}
          selected={selected}
          filterValue={query}
          items={displayedItems}
          showItemDividers
          onFilterChange={onSearchChange}
          onSelectedChange={onSelectedChange}
          message={
            displayedItems.length === 0
              ? {
                  title: Resources.repoPickerNoneFoundTitle,
                  body: Resources.repoPickerNoneFoundBody,
                  variant: 'empty',
                }
              : undefined
          }
          overlayProps={{
            width: 'small',
            onMouseDown: e => e.stopPropagation(),
            height: 'auto',
            onClickOutside: onClose,
            ...testIdProps('repo-picker-repo-list'),
          }}
        />
      ) : (
        <LoadingSkeleton variant="rounded" width="138px" height="32px" />
      )}
    </>
  )
}

RepositoryPicker.displayName = 'RepositoryPicker'
